import React, { useEffect, lazy, Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import './App.css';
import { Container, Card } from 'react-bootstrap';

import CustomNavbar from './navbar/CustomNavbar';
import BannerWithRotatingNametags from './banner/BannerWithRotatingNametags';

import background1 from "./assets/withbg.png";
import backgroundImage2 from "./assets/finaltheme.png";

// Lazy load heavy components
const ArtCardCarousel1 = lazy(() => import('./components/ArtCardCarousel/ArtCardCarousel1'));
const ArtCardCarousel2 = lazy(() => import('./components/ArtCardCarousel/ArtCardCarousel2'));
const ArtCardCarousel3 = lazy(() => import('./components/ArtCardCarousel/ArtCardCarousel3'));
const ContactSection = lazy(() => import('./contact/contact'));
const FurnitureCard = lazy(() => import('./components/FurnitureCard'));

// Loading placeholder
const LoadingPlaceholder = () => (
  <div
    style={{
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <p>Loading...</p>
  </div>
);

function FirstPage() {
  const location = useLocation();

  // Memoized styles
  const mystyle = useMemo(() => ({
    backgroundImage: `url(${background1})`,
    width: '100%',
    height: '1400px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  }), []);

  const sectionBgStyle = useMemo(() => ({
    backgroundImage: `url(${backgroundImage2})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '750px',
    margin: '0',
    marginBottom: '0',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
  }), []);

  const illustrationBgStyle = useMemo(() => ({
    backgroundImage: `url(${backgroundImage2})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '730px',
    margin: '0',
    marginBottom: '0',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
  }), []);

  // Scroll to contact
  useEffect(() => {
    if (location.state?.scrollTo === 'contact') {
      requestAnimationFrame(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Portrait image
  const portraitImage = useMemo(() => (
    <img
      src="/joseph.jpg"
      alt="Portrait"
      className="gradient-border-wrapper"
      loading="lazy"
      style={{
        width: '100%',
        maxWidth: '400px',
        height: 'auto',
        maxHeight: '400px',
        objectFit: 'cover',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderRadius: 20,
      }}
    />
  ), []);

  return (
    <div style={mystyle}>
      <div id="firstpage">
        <CustomNavbar />

        {/* Top Section */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '160px',
            marginTop: '24px',
            width: '100%',
            background: 'transparent',
            flexWrap: 'wrap',
            paddingLeft: '20px',
            paddingRight: '20px',
            boxSizing: 'border-box',
          }}
        >
          {/* Banner */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'transparent',
              flex: '0 1 auto',
            }}
          >
            <BannerWithRotatingNametags />
          </div>

          {/* Portrait */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'transparent',
              flex: '0 1 auto',
            }}
          >
            {portraitImage}
          </div>
        </div>

        {/* Video Section */}
        <Container className="d-flex justify-content-center">
          <Card
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '16px',
              height: 'auto',
              padding: '0px',
              marginTop: '0px',
              maxWidth: '90vw',
              width: '100%',
            }}
          >
            <Card.Body style={{ padding: 0 }}>
              <video
                autoPlay
                muted
                loop
                playsInline
                loading="lazy"
                style={{
                  width: '100%',
                  maxHeight: '50vh',
                  display: 'block',
                  borderRadius: '16px',
                  objectFit: 'cover',
                  margin: '0px',
                  boxShadow: '30px 30px 30px rgba(0,0,0,0.3)',
                }}
              >
                <source src="/typeportfolio.mp4" type="video/mp4" />
              </video>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Branding */}
      <section id="branding" style={sectionBgStyle}>
        <div style={{ 
          width: '100%', 
          maxWidth: '1500px',
          height: '700px',
          boxSizing: 'border-box',
          paddingLeft: '60px',
          paddingRight: '60px',
        }}>
          <h2 style={{
            fontSize: 'clamp(48px, 6vw, 72px)',
            fontWeight: 600,
            marginTop: 'clamp(20px, 5vh, 40px)',
            marginBottom: 'clamp(20px, 3vh, 30px)',
            letterSpacing: '-7px',
            paddingLeft: 25
          }}>
            <div className="custom-font">Branding</div>
          </h2>
          <Suspense fallback={<LoadingPlaceholder />}>
            <ArtCardCarousel1 />
          </Suspense>
        </div>
      </section>

      {/* Illustration */}
      <section id="illustration" style={illustrationBgStyle}>
        <div style={{ 
          width: '100%', 
          maxWidth: '1550px',
          height: '700px',
          boxSizing: 'border-box',
          paddingLeft: '60px',
          paddingRight: '60px',
        }}>
          <h2 style={{
            fontSize: 'clamp(48px, 6vw, 72px)',
            fontWeight: 600,
            marginBottom: '0px',
            letterSpacing: '-4px',
            textAlign: 'right',
            paddingRight: '30px',
          }}>
            <div className="custom-font">Illustration</div>
          </h2>
          <Suspense fallback={<LoadingPlaceholder />}>
            <ArtCardCarousel2 />
          </Suspense>
        </div>
      </section>

      {/* Book Design & Furniture */}
      <section
        id="Book Design"
        style={{
          ...illustrationBgStyle,
        }}
      >
        <div style={{ 
          maxWidth: '1550px',
          height: '700px',
          width: '100%',
          boxSizing: 'border-box',
          paddingLeft: '60px',
          paddingRight: '60px',
        }}>
          <h2
            style={{
              fontSize: 'clamp(48px, 6vw, 72px)',
              fontWeight: 600,
              margin: '40px 0 16px 0',
              letterSpacing: '-6px',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap:'220px',
                alignItems: 'baseline',
                paddingLeft: '60px'
              }}
            >
              <span className="custom-font">Book Design</span>
              <span className="custom-font">Furniture Design</span>
            </div>
          </h2>

          <Suspense fallback={<LoadingPlaceholder />}>
            <ArtCardCarousel3 />
          </Suspense>
        </div>
      </section>

      <section id="contact" style={{
        backgroundImage: `url(${backgroundImage2})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '900px',
        marginTop: '0px',
        marginBottom: '0',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
      }}>
        <div style={{ 
          maxWidth: '1550px',
          height: '900px',
          width: '100%',
          boxSizing: 'border-box',
          paddingLeft: '60px',
          paddingRight: '60px',
        }}>
          <Suspense fallback={<LoadingPlaceholder />}>
            <ContactSection />
          </Suspense>
        </div>
      </section>
    </div>

  );
}

export default FirstPage;